import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, User, ShoppingBag, ArrowUpRight, ArrowRight, ChevronDown, X, Menu } from "lucide-react";
import { useProduct } from "@/components/pdp/PdpDataContext";
import MobileMenu from "@/components/pdp/MobileMenu";

// Global premium mega-menu header (born on HP3, now shared by every HP + PDP).
// A single persistent panel opens with hover-intent, swaps inner content
// between the 5 pillars, and closes on Close ×, outside-click or Escape.
// SHOP + SKIN carry a small editorial media carousel; ROUTINES / SCIENCE /
// DISCOVER show one static editorial feature. Below lg a hamburger opens the
// slide-in MobileMenu drawer.
const A = "https://customer-assets-gfyr7b9c.emergentagent.net/job_e3513a08-18b3-459d-901e-2457595c9150/artifacts/";
const IMG = {
  mask: A + "2jk1dxzb_Mak-hidrogel.png",
  green: A + "98sma3k8_ChatGPT%20Image%20Sep%2012%2C%202026%2C%2012_49_37%20AM%20%285%29.png",
  aging: A + "q3s4ofni_ChatGPT%20Image%20Sep%205%2C%202026%2C%2012_36_02%20AM.png",
  redness: A + "ff9m18dp_ChatGPT%20Image%20Sep%205%2C%202026%2C%2012_07_31%20AM.png",
  dryness: A + "mb6kzhp9_ChatGPT%20Image%20Sep%205%2C%202026%2C%2012_28_42%20AM.png",
  rivers: "https://www.riversol.com/cdn/shop/files/Dr._Rivers_1.png?v=1774575652",
};

const ANNOUNCE = [
  "Dermatologist developed · Made for sensitive skin",
  "115,000+ happy customers",
  "4.8★ average across 30,000+ verified reviews",
  "Free shipping on orders $69+",
];

const NAV = ["Shop", "Skin", "Routines", "Science", "Discover"];
const MOBILE_NAV = [...NAV.map((title) => ({ title, link: "#" })), { title: "Skin Analyzer", link: "#" }];

const MENUS = {
  Shop: {
    type: "carousel",
    columns: [
      { eyebrow: "Featured", serif: true, links: ["Shop All", "Best Sellers", "New Arrivals", "Sets & Bundles", "Last Chance / Sale"] },
      { eyebrow: "By Product", links: ["Cleansers", "Serums & Treatments", "Moisturizers", "Eye Care", "SPF", "Masks & Peels", "Hand & Body", "Sets & Regimens"] },
      { eyebrow: "By Ingredient", links: ["Hinokitiol / Beta-T", "Retinol", "Vitamin C", "Hyaluronic Acid", "Niacinamide", "Glycolic Acid"], more: "View All Ingredients" },
    ],
    slides: [
      { image: IMG.mask, eyebrow: "New", title: ["PDRN", "Hydrogel Mask"], cta: "Shop Now" },
      { image: IMG.dryness, eyebrow: "Best Sellers", title: ["Customer", "favourites."], cta: "Shop Best Sellers" },
      { image: IMG.redness, eyebrow: "Routine Builder", title: ["Build your", "routine + save."], cta: "Build Your Routine" },
    ],
    bar: { left: "Everything for your daily skin routine.", center: "Shop all products" },
  },
  Skin: {
    type: "carousel",
    columns: [
      { eyebrow: "By Concern", links: ["Signs of Aging", "Redness & Sensitivity", "Dryness & Dehydration", "Uneven Tone & Dark Spots", "Texture & Dullness", "Acne & Blemishes", "Dark Circles & Puffiness"] },
      { eyebrow: "By Skin Type", links: ["Normal to Dry", "Very Dry", "Combination", "Oily", "Sensitive Skin", "Mature Skin"] },
      { eyebrow: "Find Your Match", links: ["Shop All Skincare", "Explore Routine Sets"], analyzer: true },
    ],
    slides: [
      { image: IMG.redness, eyebrow: "Skin-First Care", title: ["Start with", "what you need."], cta: "Explore Sensitive Skin Care" },
      { image: IMG.aging, eyebrow: "Signs of Aging", title: ["A routine built", "around your skin."], cta: "Explore Anti-Aging" },
      { image: IMG.mask, eyebrow: "Hydration", title: ["Support dry,", "dehydrated skin."], cta: "Explore Hydration" },
    ],
    bar: { left: "A clear starting point for every skin journey.", center: "Explore skincare" },
  },
  Routines: {
    type: "static",
    layout: "routines",
    columns: [
      { eyebrow: "Your Routine", serif: true, links: ["Build Your Routine + Save", "Take the Skin Analyzer", "Shop All Routine Sets"] },
      { eyebrow: "Shop by Step", steps: [
        { n: "01", name: "Preparation", sub: "Cleansers & preparation" },
        { n: "02", name: "Treatment", sub: "Serums, peels & targeted treatments" },
        { n: "03", name: "Hydration", sub: "Moisturizers & hydration" },
        { n: "04", name: "Protection", sub: "SPF & protection" },
      ] },
      { eyebrow: "Popular Routines", links: ["Anti-Aging Routine", "Redness Control Routine", "Hydration Routine", "Sensitive Skin Routine"] },
    ],
    feature: { image: IMG.mask, eyebrow: "The Riversol Routine", title: ["Four steps.", "Made to work together."], cta: "Build Your Routine", cover: true },
    bar: { left: "A complete routine, built around your skin.", center: "Explore routine sets" },
  },
  Science: {
    type: "static",
    columns: [
      { eyebrow: "Riversol Science", serif: true, links: ["Our Approach", "Clinical Dermatology", "Results / Before & After", "Formulation Standards"] },
      { eyebrow: "Key Ingredients", links: ["Hinokitiol / Beta-T", "Retinol", "Vitamin C", "Ingredient Library"] },
      { eyebrow: "Expertise", links: ["Dr. Jason Rivers", "Dermatologist Developed", "Skin Analyzer Science"] },
    ],
    feature: { image: IMG.green, eyebrow: "Signature Ingredient", title: ["One tree.", "Decades of research."], sub: "Hinokitiol / Beta-T", cta: "Discover the Science", cover: true, dark: true },
    bar: { left: "Dermatology first. Formulation with purpose.", center: "Explore our science" },
  },
  Discover: {
    type: "static",
    columns: [
      { eyebrow: "Riversol", serif: true, links: ["Our Story", "Meet Dr. Rivers", "Made in Canada", "Our Standards"] },
      { eyebrow: "Learn", links: ["Skincare Journal", "How-To Guides", "Ingredient Guide", "FAQ"] },
      { eyebrow: "Community", links: ["Customer Reviews", "Customer Stories", "In the Press", "Riversol+", "Find a Store"] },
    ],
    feature: { image: IMG.rivers, eyebrow: "Meet the Founder", title: ["The dermatologist", "behind Riversol."], cta: "Meet Dr. Rivers", cover: true, dark: true },
    bar: { left: "Get to know the people and thinking behind Riversol.", center: "Our story" },
  },
};

const HP_LINKS = [
  { label: "HP1", href: "/" }, { label: "HP2", href: "/hp2" }, { label: "HP3", href: "/hp3" }, { label: "HP4", href: "/hp4" },
];
const PDP_LINKS = [
  { label: "PDP2", href: "/pdp-concept-v2" }, { label: "PDP3", href: "/pdp-concept-v3" }, { label: "PDP3.1", href: "/pdp-concept-v3-1" }, { label: "PDP3.2", href: "/pdp-concept-v3-2" }, { label: "PDP3.3", href: "/pdp-concept-v3-3" }, { label: "PDP3.4", href: "/pdp-concept-v3-4" },
];

const Eyebrow = ({ children }) => (
  <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-neutral-400 mb-6">{children}</div>
);

const ColLink = ({ children, serif }) => (
  <li>
    <a href="#" className={serif
      ? "font-editorial text-[22px] leading-[1.75] text-neutral-900 hover:text-riversol-blue transition-colors"
      : "text-[15px] leading-[1.5] text-neutral-700 hover:text-riversol-blue transition-colors"}>
      {children}
    </a>
  </li>
);

// One editorial media panel — used for both carousel slides and static features.
const MediaFeature = ({ data }) => (
  <a href="#" className="group relative block w-full h-full overflow-hidden bg-neutral-100">
    <img src={data.image} alt="" draggable={false}
      className={`absolute inset-0 w-full h-full ${data.cover === false ? "object-contain p-8" : "object-cover"} transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]`} />
    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
    <div className="absolute inset-0 p-7 flex flex-col justify-end">
      <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/75 mb-2.5">{data.eyebrow}</div>
      <div className="font-editorial text-white text-[27px] leading-[1.08]">
        {data.title.map((t, i) => <span key={i} className="block">{t}</span>)}
      </div>
      {data.sub && <div className="mt-2 text-[12px] text-white/80">{data.sub}</div>}
      <div className="mt-4 inline-flex items-center gap-1.5 text-[12px] tracking-[0.02em] text-white font-medium">
        {data.cta}
        <ArrowUpRight size={14} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </div>
    </div>
  </a>
);

export const MegaHeaderV3 = () => {
  const { logo, v32 } = useProduct();
  const location = useLocation();
  const [open, setOpen] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [slide, setSlide] = useState(0);
  const [ann, setAnn] = useState(0);
  const [hpOpen, setHpOpen] = useState(false);
  const [pdpOpen, setPdpOpen] = useState(false);
  const openT = useRef();
  const closeT = useRef();
  const switcherRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // PDP3.2 / 3.3: hide on scroll-down while reading; once the hero is passed
  // stay hidden so PdpStickyNav takes over the top edge. Restore near top / on scroll-up.
  useEffect(() => {
    if (!v32) return;
    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const hero = document.querySelector('[data-testid="hero-section"]');
      const heroBottom = hero ? hero.getBoundingClientRect().bottom : Infinity;
      if (y <= 8) setHidden(false);
      else if (heroBottom <= 48) setHidden(true);
      else if (y > last + 2) setHidden(true);
      else if (y < last - 2) setHidden(false);
      last = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [v32]);

  useEffect(() => { const id = setInterval(() => setAnn((a) => (a + 1) % ANNOUNCE.length), 3500); return () => clearInterval(id); }, []);
  useEffect(() => { setSlide(0); }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setOpen(null); setHpOpen(false); setPdpOpen(false); } };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (switcherRef.current && !switcherRef.current.contains(e.target)) { setHpOpen(false); setPdpOpen(false); } };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const scheduleOpen = useCallback((key) => {
    clearTimeout(closeT.current);
    setOpen((cur) => {
      if (cur) return key; // instant swap while shell already open
      clearTimeout(openT.current);
      openT.current = setTimeout(() => setOpen(key), 130);
      return cur;
    });
  }, []);
  const scheduleClose = useCallback(() => {
    clearTimeout(openT.current);
    clearTimeout(closeT.current);
    closeT.current = setTimeout(() => setOpen(null), 160);
  }, []);
  const cancelClose = useCallback(() => clearTimeout(closeT.current), []);
  const closeNow = () => { clearTimeout(openT.current); clearTimeout(closeT.current); setOpen(null); };

  const announceHidden = scrolled;
  const cfg = open ? MENUS[open] : null;

  return (
    <header
      className={`sticky top-0 z-[60] ${v32 ? "transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]" : ""}`}
      style={v32 && hidden && !open ? { transform: "translateY(-100%)" } : undefined}
      data-testid="mega-header-v3"
    >
      {/* Rotating announcement */}
      <div data-testid="header-announcement"
        className={`bg-riversol-blue text-white text-center overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${announceHidden ? "max-h-0 opacity-0 py-0" : "max-h-16 opacity-100 py-2.5"}`}>
        <AnimatePresence mode="wait">
          <motion.div key={ann} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.35 }}
            className="text-[11px] tracking-[0.18em] uppercase font-mono">
            {ANNOUNCE[ann]}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bar */}
      <div className={`relative z-[62] bg-white transition-shadow duration-300 ${scrolled && !open ? "shadow-[0_2px_20px_rgba(0,0,0,0.04)]" : ""} ${open ? "" : "border-b border-neutral-200"}`}>
        <div className="relative max-w-[1600px] mx-auto px-6 lg:px-10 h-[76px] flex items-center justify-between">
          {/* Logo */}
          <a href="/" data-testid="header-logo" className="shrink-0 flex items-center">
            <img src={logo} alt="Riversol" className="h-7 w-auto" />
          </a>

          {/* Primary nav — centered on the header */}
          <nav className="hidden lg:flex items-center gap-9 absolute left-1/2 -translate-x-1/2">
            {NAV.map((title) => {
              const key = title;
              const active = open === key;
              return (
                <button key={key} type="button"
                  data-testid={`nav-${title.toLowerCase()}`}
                  aria-expanded={active} aria-haspopup="true"
                  onMouseEnter={() => scheduleOpen(key)}
                  onMouseLeave={scheduleClose}
                  onFocus={() => scheduleOpen(key)}
                  onClick={() => setOpen(key)}
                  className="relative py-2 text-[14px] text-neutral-800 hover:text-riversol-blue transition-colors">
                  {title}
                  <span className={`absolute left-0 -bottom-0.5 h-[1.5px] bg-riversol-blue transition-all duration-200 ${active ? "w-full" : "w-0"}`} />
                </button>
              );
            })}
          </nav>

          {/* Utilities */}
          <div className="hidden lg:flex items-center gap-6 shrink-0 text-neutral-800">
            <a href="#" data-testid="nav-skin-analyzer" className="inline-flex items-center gap-1 text-[13px] hover:text-riversol-blue transition-colors">
              Skin Analyzer <ArrowUpRight size={14} strokeWidth={1.8} />
            </a>
            <button aria-label="Search" data-testid="header-search" className="hover:text-riversol-blue transition-colors"><Search size={19} strokeWidth={1.6} /></button>
            <button aria-label="Account" data-testid="header-account" className="hover:text-riversol-blue transition-colors"><User size={19} strokeWidth={1.6} /></button>
            <button aria-label="Cart" data-testid="header-cart" className="relative hover:text-riversol-blue transition-colors">
              <ShoppingBag size={19} strokeWidth={1.6} />
              <span className="absolute -top-1.5 -right-2 bg-riversol-blue text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-mono">0</span>
            </button>
            {/* Discreet internal variant switcher */}
            <div className="flex items-center gap-2 pl-5 ml-1 border-l border-neutral-200 relative" ref={switcherRef}>
              {[["HP", hpOpen, setHpOpen, setPdpOpen, HP_LINKS, "hps"], ["PDP", pdpOpen, setPdpOpen, setHpOpen, PDP_LINKS, "pdps"]].map(([label, isOpen, setThis, setOther, links, tid]) => (
                <div key={label} className="relative">
                  <button type="button" data-testid={`nav-${tid}`}
                    onClick={() => { setThis((o) => !o); setOther(false); }}
                    className="inline-flex items-center gap-0.5 font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-400 hover:text-riversol-blue transition-colors">
                    {label}<ChevronDown size={11} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div data-testid={`${tid}-dropdown`} className="absolute right-0 top-full mt-2 min-w-[120px] bg-white border border-neutral-200 shadow-[0_8px_24px_rgba(15,46,46,0.10)] rounded-md py-1.5 z-[70]">
                      {links.map((p) => {
                        const cur = location.pathname === p.href;
                        return (
                          <a key={p.label} href={p.href} data-testid={`${tid}-item-${p.label.toLowerCase().replace(/\./g, "-")}`}
                            className={`block px-4 py-2 text-[12px] transition-colors ${cur ? "text-riversol-blue font-medium bg-riversol-blue/[0.04]" : "text-neutral-700 hover:text-riversol-blue hover:bg-neutral-50"}`}>
                            {p.label}
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Mobile utilities */}
          <div className="flex lg:hidden items-center gap-4 shrink-0 text-neutral-800">
            <button aria-label="Search" data-testid="header-search-mobile" className="hover:text-riversol-blue transition-colors"><Search size={19} strokeWidth={1.6} /></button>
            <button aria-label="Cart" data-testid="header-cart-mobile" className="relative hover:text-riversol-blue transition-colors">
              <ShoppingBag size={19} strokeWidth={1.6} />
              <span className="absolute -top-1.5 -right-2 bg-riversol-blue text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-mono">0</span>
            </button>
            <button type="button" aria-label="Open menu" aria-expanded={menuOpen} onClick={() => setMenuOpen(true)}
              data-testid="header-menu-toggle" className="ml-1 hover:text-riversol-blue transition-colors">
              <Menu size={21} strokeWidth={1.6} />
            </button>
          </div>
        </div>
        {open && cfg && (
            <div
              onMouseEnter={cancelClose} onMouseLeave={scheduleClose}
              className="absolute left-0 right-0 top-full bg-white border-t border-neutral-100 shadow-[0_16px_40px_-24px_rgba(15,46,46,0.25)] z-[61]"
              data-testid="mega-panel">
              <div key={open}
                data-testid={`mega-panel-${open.toLowerCase()}`}>
                  <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-12">
                    <div className="flex gap-12 min-h-[420px]">
                      {/* Columns */}
                      <div className={`flex-1 grid gap-10 ${cfg.layout === "routines" ? "grid-cols-3" : "grid-cols-3"}`}>
                        {cfg.columns.map((col, ci) => (
                          <div key={ci}>
                            <Eyebrow>{col.eyebrow}</Eyebrow>
                            {col.steps ? (
                              <ul className="space-y-6">
                                {col.steps.map((s) => (
                                  <li key={s.n}>
                                    <a href="#" className="group flex gap-4">
                                      <span className="font-editorial text-[20px] text-neutral-300 leading-none pt-0.5">{s.n}</span>
                                      <span>
                                        <span className="block text-[15px] text-neutral-900 group-hover:text-riversol-blue transition-colors">{s.name}</span>
                                        <span className="block text-[12px] text-neutral-500 mt-0.5">{s.sub}</span>
                                      </span>
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <ul className={col.serif ? "space-y-0" : "space-y-3.5"}>
                                {col.links.map((l) => <ColLink key={l} serif={col.serif}>{l}</ColLink>)}
                              </ul>
                            )}
                            {col.more && (
                              <a href="#" className="group inline-flex items-center gap-1.5 mt-5 text-[12px] tracking-[0.02em] text-riversol-blue border-b border-riversol-blue/40 pb-1 hover:border-riversol-blue transition-colors">
                                {col.more}<ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
                              </a>
                            )}
                            {col.analyzer && (
                              <div className="mt-8 bg-riversol-blue/[0.06] p-6 max-w-[320px]" data-testid="mega-analyzer-card">
                                <div className="font-editorial text-[19px] leading-[1.25] text-neutral-900">A little guidance.<br />A personal starting point.</div>
                                <a href="#" className="group mt-4 inline-flex items-center gap-1.5 text-[12px] text-riversol-blue font-medium">
                                  Take the Skin Analyzer <ArrowUpRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </a>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Media */}
                      <div className="w-[30%] max-w-[440px] shrink-0">
                        {cfg.type === "carousel" ? (
                          <div className="h-full flex flex-col" data-testid="mega-carousel">
                            <div className="group relative flex-1 min-h-[360px] overflow-hidden">
                              <AnimatePresence mode="wait">
                                <motion.div key={slide} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="absolute inset-0">
                                  <MediaFeature data={cfg.slides[slide]} />
                                </motion.div>
                              </AnimatePresence>
                              {/* hover arrows */}
                              <button aria-label="Previous" data-testid="mega-carousel-prev"
                                onClick={(e) => { e.stopPropagation(); setSlide((s) => (s - 1 + cfg.slides.length) % cfg.slides.length); }}
                                className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/85 text-neutral-800 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                <ArrowRight size={15} className="rotate-180" />
                              </button>
                              <button aria-label="Next" data-testid="mega-carousel-next"
                                onClick={(e) => { e.stopPropagation(); setSlide((s) => (s + 1) % cfg.slides.length); }}
                                className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/85 text-neutral-800 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                <ArrowRight size={15} />
                              </button>
                            </div>
                            <div className="flex items-center justify-center gap-2 pt-4">
                              {cfg.slides.map((_, i) => (
                                <button key={i} aria-label={`Slide ${i + 1}`} data-testid={`mega-dot-${i}`}
                                  onClick={() => setSlide(i)}
                                  className={`h-1.5 rounded-full transition-all duration-200 ${i === slide ? "w-6 bg-riversol-blue" : "w-1.5 bg-neutral-300 hover:bg-neutral-400"}`} />
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="h-full min-h-[420px]" data-testid="mega-feature">
                            <MediaFeature data={cfg.feature} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Bottom bar */}
                  <div className="border-t border-neutral-100 bg-neutral-50/50">
                    <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-6">
                      <span className="text-[13px] text-neutral-500">{cfg.bar.left}</span>
                      <a href="#" className="group inline-flex items-center gap-1.5 text-[13px] text-neutral-800 hover:text-riversol-blue transition-colors">
                        {cfg.bar.center} <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                      </a>
                      <button type="button" onClick={closeNow} data-testid="mega-close" className="inline-flex items-center gap-1 text-[12px] tracking-[0.08em] uppercase font-mono text-neutral-400 hover:text-neutral-800 transition-colors">
                        Close <X size={13} />
                      </button>
                    </div>
                  </div>
                </div>
            </div>
          )}
      </div>

      {/* outside-click backdrop */}
      {open && (
          <div
            onClick={closeNow} className="fixed inset-0 top-0 bg-black/20 z-[55] animate-[fadeIn_0.2s_ease]" data-testid="mega-backdrop" />
      )}
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} nav={MOBILE_NAV} pdpLinks={PDP_LINKS} hpLinks={HP_LINKS} currentPath={location.pathname} />
    </header>
  );
};

export default MegaHeaderV3;
